import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Column } from "../../../../core/components/Column";
import { NotificationProps } from "../interfaces/NotificationInterface";
import { TextComponent } from "../../../../core/components/TextComponent";
import { Row } from "../../../../core/components/Row";
import { CustomButton } from "../../../../core/components/CustomButton";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../core/store/store";
import { CardNotification } from "../components/CardNotification";
import { Like } from "../../../../core/icons/Like";
import { Match } from "../../../../core/icons/Match";
import { Alert } from "../../../../core/icons/Alert";
import { XCloseGray } from "../../../../core/icons/XClose";
import { Container } from "../../../../core/components/Container";
import { ArrowDown2 } from "../../../../core/icons/ArrowDown";
import { onGetLikes, onGetMyLikes, onGetMyMatches, onGetUserInfoById } from "../../../match/presentation/store/MatchThunk";
import { Likes } from "../../../match/domain/entities/userMatch";
import { alertReceived, deleteMyNotificationReceived, myNotificationReceived } from "../../../match/presentation/store/MatchSlice";
import { UserInfoProfile } from "../../../match/domain/entities/userInfoProfile";
import { ShowUserInfoProfile } from "../components/ShowUserInfoProfile";
import { onGetNotification } from "../store/NotificationThunk";
import InfoNotificationAdmin from "../../domain/entities/InfoNotificationAdmin";
import { ModalRestricted } from "../../../chat/presentation/components/ModalRestricted";
import ReactDOM from "react-dom";
import CircularProgress from "../../../../core/components/CircularProgress";
import { getMatchLocationParams } from "../../../../core/utilities/locationStorage";
import { HiddenLikePreview, LikesYouResponse } from "../../../match/domain/entities/likesYou";
import { HiddenLikesPreview } from "../components/HiddenLikesPreview";

const PAGELIMIT = "10";

const normalizeCoordinate = (
    value: string | number | null | undefined,
    minimum: number,
    maximum: number,
) => {
    const rawValue = String(value ?? "").trim();
    if (!rawValue) return "";
    const numeric = Number(rawValue);
    if (!Number.isFinite(numeric) || numeric < minimum || numeric > maximum) {
        return "";
    }
    return numeric.toString();
};

export const NotificationScreen: React.FC<NotificationProps> = ({
    hiddenNotification = false,
    handleNotification = () => {},
    fullPage = false,
}) => {
    const navigate = useNavigate();
    const { myNotifications } = useSelector((state: RootState) => state.match);
    const signedInUser = useSelector((state: RootState) => state.onboarding.user);
    const dispatch: AppDispatch = useDispatch();
    const [activeCategory, setActiveCategory] = useState<"like" | "match" | "alert" | "myLikes">("like");
    const [page, setPage] = useState(1);
    const [loadingMore, setLoadingMore] = useState(false);
    const [likesYouAccess, setLikesYouAccess] = useState<LikesYouResponse["access"]>();
    const [likesYouCount, setLikesYouCount] = useState(0);
    const [hiddenLikes, setHiddenLikes] = useState<HiddenLikePreview[]>([]);
    const [likesYouErrorPage, setLikesYouErrorPage] = useState<number>();
    const likesYouRequestGeneration = useRef(0);

    const [canLoadMoreLikes, setCanLoadMoreLikes] = useState(true);
    const [canLoadMoreMyLikes, setCanLoadMoreMyLikes] = useState(true);
    const [canLoadMoreMyMatches, setCanLoadMoreMyMatches] = useState(true);
    const [canLoadMoreAlert, setCanLoadMoreAlert] = useState(true);

    const setCanLoadMoreMap = {
        like: setCanLoadMoreLikes,
        match: setCanLoadMoreMyMatches,
        myLikes: setCanLoadMoreMyLikes
    }
   
    const [userInfoProfile, setUserInfoProfile] = useState<Partial<Likes | undefined>>(undefined);
    const [openUserInfoProfile, setOpenUserInfoProfile] = useState<boolean>(false);

    const [actionNotFound, setActionNotFound] = useState(false);
    const showInlineProfileDetail = fullPage && openUserInfoProfile;

    const notificationActions = {
        like: () => handleViewNotification("like", 1),
        match: () => handleViewNotification("match", 1),
        alert: () => handleViewNotificationAlert(1),
        myLikes: () => handleViewNotification("myLikes", 1)
    };

    const handleViewNotification = async (
        type: "match" | "like" | "myLikes",
        page: number,
    ) => {
        const requestGeneration = type === "like"
            ? ++likesYouRequestGeneration.current
            : undefined;
        const requestSession = type === "like"
            ? window.localStorage.getItem("userToken")
            : undefined;
        if (type === "like") setLikesYouErrorPage(undefined);
        setLoadingMore(true);
        const storedLocation = getMatchLocationParams();
        const savedRealLat = normalizeCoordinate(signedInUser?.lat, -90, 90);
        const savedRealLng = normalizeCoordinate(signedInUser?.lng, -180, 180);
        const savedOverrideLat = normalizeCoordinate(signedInUser?.discovery_override_lat, -90, 90);
        const savedOverrideLng = normalizeCoordinate(signedInUser?.discovery_override_lng, -180, 180);
        const storedSearchLat = normalizeCoordinate(storedLocation.user_lat, -90, 90);
        const storedSearchLng = normalizeCoordinate(storedLocation.user_lng, -180, 180);
        const storedDistanceLat = normalizeCoordinate(storedLocation.distance_origin_lat, -90, 90);
        const storedDistanceLng = normalizeCoordinate(storedLocation.distance_origin_lng, -180, 180);
        const locationParams = {
            user_lat: storedSearchLat || savedOverrideLat || savedRealLat,
            user_lng: storedSearchLng || savedOverrideLng || savedRealLng,
            distance_origin_lat: storedDistanceLat || savedRealLat,
            distance_origin_lng: storedDistanceLng || savedRealLng,
        };
        const params = {
            params: {
                ...locationParams,
                page: page.toString(),
                limit: PAGELIMIT,
            }
        };
        const result = type === "like"
            ? await dispatch(onGetLikes(params))
            : type === "match"
                ? await dispatch(onGetMyMatches(params))
                : await dispatch(onGetMyLikes(params));
        if (
            type === "like" &&
            (requestGeneration !== likesYouRequestGeneration.current ||
                requestSession !== window.localStorage.getItem("userToken"))
        ) {
            return;
        }
        setLoadingMore(false);
        if (!("payload" in result) || !result.payload) {
            console.error(`Error al obtener ${type}`);
            if (type === "like") setLikesYouErrorPage(page);
            return;
        }
        if (type === "like") {
            const likesYou = result.payload as LikesYouResponse;
            setLikesYouAccess(likesYou.access);
            setLikesYouCount(likesYou.count);

            if (likesYou.access === "hidden") {
                setHiddenLikes((currentLikes) => page === 1
                    ? likesYou.likes
                    : [...currentLikes, ...likesYou.likes]
                );
            } else if (likesYou.can_respond_directly && likesYou.likes.length > 0) {
                dispatch(myNotificationReceived({ type: "like", data: likesYou.likes }));
            }

            if (likesYou.likes.length < Number(PAGELIMIT)) setCanLoadMoreLikes(false);
            return;
        }

        const resultMyNotification = result.payload as Likes[];
        if (resultMyNotification.length > 0) dispatch(myNotificationReceived({ type: type, data: resultMyNotification }));
        if (resultMyNotification.length < Number(PAGELIMIT)) setCanLoadMoreMap[type](false);
    }

    const handleViewNotificationAlert = async (page: number) => {
        const params = {
            params: { page: page.toString(), limit: PAGELIMIT }
        };
        setLoadingMore(true);
        const result = await dispatch(onGetNotification(params));
        setLoadingMore(false);
        const resultMyNotification = result?.payload as InfoNotificationAdmin[];
        if (resultMyNotification && resultMyNotification.length > 0) dispatch(alertReceived({ data: resultMyNotification }));
        if (!resultMyNotification || resultMyNotification.length < Number(PAGELIMIT)) {
            setCanLoadMoreAlert(false);
        }
    };
    
    const handlePagination = () => {
        switch (activeCategory) {
            case "like":
                if (!canLoadMoreLikes) return;
                setPage((prev) => prev + 1)
                break;
            case "match":
                if (!canLoadMoreMyMatches) return;
                setPage((prev) => prev + 1);
                break;
            case "alert":
                if (!canLoadMoreAlert) return;
                setPage((prev) => prev + 1);
                break;
            case "myLikes":
                if (!canLoadMoreMyLikes) return;
                setPage((prev) => prev + 1);
                break;
        }
    }

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollTop + clientHeight >= scrollHeight - 10 && !loadingMore) handlePagination();
    };

    useEffect(() => {
        if (page === 1) return;
        const notificationActions2 = {
            like: () => handleViewNotification("like", page),
            myLikes: () => handleViewNotification("myLikes", page),
            match: () => handleViewNotification("match", page),
            alert: () => handleViewNotificationAlert(page),
        };
        notificationActions2[activeCategory]?.();
    }, [page]);

    useEffect(() => {
        return () => {
            likesYouRequestGeneration.current += 1;
        };
    }, []);

    useEffect(() => {
        setPage(1);
        setCanLoadMoreLikes(true);
        setCanLoadMoreMyMatches(true);
        setCanLoadMoreMyLikes(true);
        setCanLoadMoreAlert(true);
        setOpenUserInfoProfile(false);
        setLikesYouAccess(undefined);
        setLikesYouCount(0);
        setHiddenLikes([]);
        setLikesYouErrorPage(undefined);
        dispatch(deleteMyNotificationReceived());
        notificationActions[activeCategory]();
    }, [dispatch, activeCategory]);

    const filteredNotifications = activeCategory === "like"
        ? (likesYouAccess === "revealed" ? myNotifications.like : [])
        : activeCategory === "match"
            ? myNotifications.match
            : activeCategory === "myLikes"
                ? myNotifications.myLikes
                : activeCategory === "alert"
                    ? myNotifications.alert
                    : [];

    useEffect(() => {
        if (filteredNotifications.length !== 7) return;
        handlePagination();
    }, [filteredNotifications.length]);

    const showHiddenLikes = activeCategory === "like" && likesYouAccess === "hidden" && likesYouCount > 0;
    const showLikesYouError = activeCategory === "like" && likesYouErrorPage !== undefined;

    const handleShowUserProfile = async (user_id: string) => {
        if( activeCategory === "alert") return;
        if (!myNotifications[activeCategory]) return;
        const infoUserProfile: Partial<Likes> | undefined = myNotifications[activeCategory]?.find((notifications) => notifications.user_id === user_id);
        setUserInfoProfile(myNotifications[activeCategory]?.find((notifications) => notifications.user_id === user_id));
        if (infoUserProfile?.status === "banned" || infoUserProfile?.status === "inactive") {
            setActionNotFound(true);
            return;
        }
        if (!infoUserProfile) {
            const locationParams = getMatchLocationParams();
            const result = await dispatch(onGetUserInfoById({
                params : {
                    user_id,
                    ...locationParams,
                }
            }));
            setUserInfoProfile(result.payload as UserInfoProfile);
        }
        setOpenUserInfoProfile(true);
    }

    return (
        <>
            {openUserInfoProfile && !showInlineProfileDetail && 
                <ShowUserInfoProfile
                    activeCategory={activeCategory}
                    userInfoProfile={userInfoProfile}
                    setUserInfoProfile={setUserInfoProfile}
                    handleProfilePreview={() => setOpenUserInfoProfile(false)}
                    handleNotification={handleNotification}
                />
            }
            {ReactDOM.createPortal(
                <ModalRestricted 
                    userInfoProfile={{ user_id: userInfoProfile?.user_id, username: userInfoProfile?.username, match_id: userInfoProfile?.match_id } as Likes}
                    action={actionNotFound}
                    category={activeCategory}
                    setAction={setActionNotFound}
                />,
				document.getElementById("root") as HTMLElement
			)}
            <aside
                className={
                    fullPage
                        ? "notification-scroll-scope w-full h-full bg-white rounded-xl shadow-sm overflow-y-auto overflow-x-hidden scrollbar-hide"
                        : `fixed top-0 left-0 z-20 w-full h-dvh md:h-full md:w-[28.4rem] bg-white transition-transform duration-300 ease-out transform ${hiddenNotification ? "translate-x-full" : "translate-x-0"} md:translate-x-0 shadow-lg -translate-x-2`
                }
                onScroll={fullPage ? handleScroll : undefined}
            >

                <Column className={`${fullPage ? "h-full" : "h-full"} flex flex-col`}>
                    {!fullPage && (
                    <button className="block md:hidden absolute top-4 right-4 p-2 text-gray-600 hover:text-gray-800" onClick={handleNotification} aria-label="Close notifications">
                        <XCloseGray />
                    </button>
                    )}

                    <Row
                        className="p-6 font-semibold border-b border-gray-300 mt-2 gap-3"
                        crossAxisAlignment="center"
                    >
                        {fullPage && (
                            <button
                                type="button"
                                className="w-10 aspect-square flex items-center justify-center text-primary transform rotate-90 rounded-full hover:bg-grayDefault"
                                onClick={() => navigate("/")}
                                aria-label="Back to home"
                            >
                                <ArrowDown2 />
                            </button>
                        )}
                        <TextComponent variant="h4" className="font-semibold">
                            Notification
                        </TextComponent>
                    </Row>

                    <Column className="gap-5 p-6 border-b border-gray-300 ">
                        <Row mainAxisAlignment="center">
                            <CustomButton
                                className="w-full mr-4"
                                color={activeCategory === "match" ? "primary" : "gray"}
                                type="button"
                                rounded
                                small
                                onClick={() => setActiveCategory("match")}
                            >
                                <div className="pr-2">
                                    <Match color={activeCategory === "match" ? "White" : "Default"} />
                                </div>
                                Matches
                            </CustomButton>
                            <CustomButton
                                type="button"
                                rounded
                                small
                                className="w-full mr-4"
                                color={(activeCategory === "like" || activeCategory === "myLikes") ? "primary" : "gray"}
                                onClick={() => setActiveCategory("like")}
                            >
                                <div className="pr-2">
                                    <Like color={(activeCategory === "like" || activeCategory === "myLikes") ? "White" : "Default"} />
                                </div>
                                Like
                            </CustomButton>
                            <CustomButton
                                color={activeCategory === "alert" ? "primary" : "gray"}
                                type="button"
                                rounded
                                small
                                className="w-full"
                                onClick={() => setActiveCategory("alert")}
                            >
                                <div className="pr-2">
                                    <Alert color={activeCategory === "alert" ? "White" : "Default"} />
                                </div>
                                Alerts
                            </CustomButton>
                        </Row>
                    </Column>

                    {(activeCategory === "like" || activeCategory === "myLikes") && (
                        <Row mainAxisAlignment="between" className=" pt-5 px-5">
                            <CustomButton
                                className="w-full h-10 mr-5"
                                color={activeCategory === "like" ? "primary" : "gray"}
                                type="button"
                                small
                                onClick={() => { setActiveCategory("like"); }}
                            >
                                <div className="pr-4">
                                    <Like color={activeCategory === "like" ? "White" : "Default"} />
                                </div>
                                Likes You
                            </CustomButton>
                            <CustomButton
                                className="w-full h-10"
                                color={activeCategory === "myLikes" ? "primary" : "gray"}
                                type="button"
                                small
                                onClick={() => { setActiveCategory("myLikes") }}
                            >
                                <div className="pr-7">
                                    <Match color={activeCategory === "myLikes" ? "White" : "Default"} />
                                </div>
                                My likes
                            </CustomButton>
                        </Row>
                    )}

                    <Column className={fullPage ? "p-5" : "p-5 flex-grow overflow-y-auto"}>
                        {showInlineProfileDetail ? (
                            <ShowUserInfoProfile
                                activeCategory={activeCategory}
                                userInfoProfile={userInfoProfile}
                                setUserInfoProfile={setUserInfoProfile}
                                handleProfilePreview={() => setOpenUserInfoProfile(false)}
                                handleNotification={handleNotification}
                                desktopFullPage
                            />
                        ) : (
                            <div
                                className={fullPage ? "flex flex-col md:pb-5" : "flex flex-col md:pb-5 flex-grow overflow-y-auto overflow-x-hidden"}
                                onScroll={fullPage ? undefined : handleScroll}
                                key={activeCategory}
                            >
                                {showLikesYouError ? (
                                    <Column
                                        mainAxisAlignment="center"
                                        crossAxisAlignment="center"
                                        className="gap-4 flex-grow text-center select-none"
                                    >
                                        <TextComponent variant="h4">
                                            Likes You could not be loaded. Please try again.
                                        </TextComponent>
                                        <CustomButton
                                            type="button"
                                            color="primary"
                                            onClick={() => handleViewNotification("like", likesYouErrorPage ?? 1)}
                                        >
                                            Retry Likes You
                                        </CustomButton>
                                    </Column>
                                ) : showHiddenLikes ? (
                                    <HiddenLikesPreview
                                        count={likesYouCount}
                                        likes={hiddenLikes}
                                        onSubscribe={() => navigate("/subscription")}
                                        canLoadMore={canLoadMoreLikes}
                                        loadingMore={loadingMore}
                                        onLoadMore={handlePagination}
                                    />
                                ) : (
                                    filteredNotifications.map((notification, index) => (
                                        <CardNotification key={index} notification={notification} showProfilePicture={activeCategory !== "alert"} category={activeCategory} showInfoProfile={handleShowUserProfile} />
                                    ))
                                )}
                                {loadingMore ?
                                    <CircularProgress size={48}/>
                                    :
                                    <>
                                        {filteredNotifications.length === 0 && !showHiddenLikes && !showLikesYouError &&
                                            <Column
                                                mainAxisAlignment="center"
                                                crossAxisAlignment="center" 
                                                className="gap-2 flex-grow text-center select-none"
                                            >
                                                <img
                                                    src="/search.png"
                                                    className="w-24"
                                                    alt="loadingNotification"
                                                    draggable={false}
                                                />
                                                <TextComponent variant="h4">
                                                    {activeCategory === "like" && (
                                                        <>You haven't received any <span className="text-primary">likes</span> yet</>
                                                    )}
                                                    {activeCategory === "match" && (
                                                        <>No <span className="text-primary">matches</span> with anyone yet</>
                                                    )}
                                                    {activeCategory === "myLikes" && (
                                                        <>You haven't <span className="text-primary">liked</span> anyone yet</>
                                                    )}
                                                    {activeCategory === "alert" && (
                                                        <>You currently have no <span className="text-primary">alerts</span></>
                                                    )}
                                                </TextComponent>
                                            </Column>
                                        }
                                    </>
                                }
                            </div>
                        )}
                    </Column>

                    {activeCategory === "match" && !showInlineProfileDetail && (
                        <Container className="bg-[#FCE7F7E5] rounded-lg p-4 mb-5 md:mb-10 mx-5">
                            <Column crossAxisAlignment="center">
                                <TextComponent variant="h5" className="text-primary font-bold">
                                    NOTE
                                </TextComponent>
                                <TextComponent variant="h6" className="text-center">
                                    Swipe the match to the left if you would like to permanently delete it or swipe right if you would like to block this member.
                                </TextComponent>
                                <TextComponent variant="h6" className=" text-center mt-2">
                                    You can unblock the member at any time if you choose.
                                </TextComponent>
                            </Column>
                        </Container>
                    )}
                </Column>
            </aside>
        </>
    );
};
